import { X, Check, Zap, TrendingUp, Shield, Loader2, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useBillingPlans } from "../../hooks/useAPI";
import { PLAN_NAMES, UPGRADE_PATH, SALES_HREF, type RequiredPlan } from "./PlanLock";
import { trackEvent } from "../../services/analytics";

// Standard focusable selector for the focus trap (WAI-ARIA APG).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * UpgradeModal — global modal triggered by `error.status === 402`
 * or `error.code === 'UPGRADE_REQUIRED'` (see App.tsx onAPIError).
 *
 * Pre-refactor this modal carried hardcoded "Professional" / "Enterprise"
 * plan names and $499 / $999 prices that DID NOT MATCH the actual plan
 * tier system (Sandbox / Scale / Governance / Enterprise / Custom). Users
 * who triggered it from an UPGRADE_REQUIRED API error were shown plan
 * names they had never seen anywhere else in the app or pricing page.
 *
 * Now: pulls plans live from `useBillingPlans()` so the modal always
 * shows the same prices + names as the Billing page, and routes the
 * primary CTA through the canonical UPGRADE_PATH.
 */

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: "trial_ending" | "quota_exceeded" | "feature_locked" | "general";
  featureName?: string;
  /** Optional — minimum plan tier needed. When set, only plans at or
   *  above this tier are shown as upgrade targets. */
  requiredPlan?: RequiredPlan;
}

const REASON_META = {
  trial_ending: {
    title: "Your trial is ending soon",
    description: "Upgrade to keep monitoring your AI agents without interruption.",
    icon: Zap,
  },
  quota_exceeded: {
    title: "You've reached your plan limit",
    description: "Upgrade to unlock higher quotas and continue ingesting traces.",
    icon: TrendingUp,
  },
  feature_locked: {
    title: "Feature locked on your current plan",
    description: "This feature is available on higher tiers.",
    icon: Lock,
  },
  general: {
    title: "Upgrade your plan",
    description: "Compare tiers and pick the one that fits your team.",
    icon: Shield,
  },
} as const;

// Order plans should appear in (matches PLAN_ORDER from PlanLock).
const PLAN_DISPLAY_ORDER = ["scale", "governance", "enterprise", "custom"] as const;

export function UpgradeModal({
  isOpen,
  onClose,
  reason = "general",
  featureName,
  requiredPlan,
}: UpgradeModalProps) {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual");
  // Gate the query on `isOpen` so the modal — which is mounted globally in
  // App.tsx — does not fire `GET /api/billing/plans` while sitting idle on
  // unauthenticated routes like /login (would 401 → redirect-loop).
  const { data: plans, isLoading } = useBillingPlans({ enabled: isOpen });

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Capture invoker on open so ESC / close can return focus per WAI-ARIA
  // dialog pattern. Pre-fix dropped focus to <body>.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) prev.focus();
      return;
    }
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    closeButtonRef.current?.focus();

    // ESC + focus trap (matches KeyboardShortcutsModal pattern).
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const meta = REASON_META[reason];
  const Icon = meta.icon;
  const title = reason === "feature_locked" && featureName
    ? `${featureName} requires a higher tier`
    : meta.title;

  // Filter to plans at or above `requiredPlan` (when provided), and order
  // by PLAN_DISPLAY_ORDER. Falls back to all paid plans for generic
  // upgrade prompts (no specific feature was locked).
  const requiredIdx = requiredPlan
    ? PLAN_DISPLAY_ORDER.indexOf(requiredPlan as (typeof PLAN_DISPLAY_ORDER)[number])
    : 0;
  const upgradePlans = (plans ?? [])
    .filter((p) => {
      const idx = PLAN_DISPLAY_ORDER.indexOf(p.planKey as (typeof PLAN_DISPLAY_ORDER)[number]);
      return idx >= 0 && idx >= requiredIdx;
    })
    .sort((a, b) =>
      PLAN_DISPLAY_ORDER.indexOf(a.planKey as (typeof PLAN_DISPLAY_ORDER)[number]) -
      PLAN_DISPLAY_ORDER.indexOf(b.planKey as (typeof PLAN_DISPLAY_ORDER)[number])
    );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        className="adp-modal relative w-[90vw] max-w-[960px] max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 border border-border bg-surface-2">
              <Icon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 id="upgrade-modal-title" className="text-lg font-semibold text-foreground">
                {title}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-surface-2 border border-transparent hover:border-border transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
            aria-label="Close upgrade modal"
          >
            <X className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="py-6 space-y-6 flex-1 overflow-y-auto">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                billingCycle === "monthly"
                  ? "adp-btn-primary"
                  : "bg-surface-2 text-muted-foreground hover:text-foreground border border-border"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                billingCycle === "annual"
                  ? "adp-btn-primary"
                  : "bg-surface-2 text-muted-foreground hover:text-foreground border border-border"
              }`}
            >
              Annual
              <span className="px-2 py-0.5 adp-btn-primary text-[10px] font-bold uppercase tracking-wider">
                Save 17%
              </span>
            </button>
          </div>

          {/* Loading state — plans haven't resolved yet */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mb-3" aria-hidden="true" />
              <p className="text-xs text-muted-foreground">Loading plans…</p>
            </div>
          )}

          {/* Plans Grid */}
          {!isLoading && upgradePlans.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6">
              {upgradePlans.map((plan, idx) => {
                const isFirst = idx === 0;
                const price = billingCycle === "monthly" ? plan.priceMonthly : plan.priceAnnual;
                const planLabel = PLAN_NAMES[plan.planKey as RequiredPlan] ?? plan.name;
                return (
                  <div
                    key={plan.planKey}
                    className={`p-6 border-2 ${
                      isFirst ? "border-border bg-surface-2" : "border-border bg-surface-1"
                    } relative`}
                  >
                    {isFirst && (
                      <div className="absolute top-0 right-6 -translate-y-1/2">
                        <span className="px-3 py-1 adp-btn-primary text-[10px] font-bold uppercase tracking-wider">
                          Recommended
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-foreground mb-2">{planLabel}</h3>
                      {plan.priceMonthly > 0 ? (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-mono text-foreground">
                              €{price}
                            </span>
                            <span className="text-sm text-muted-foreground">/month</span>
                          </div>
                          {billingCycle === "annual" && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Billed annually (€{price * 12}/year)
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Custom pricing — contact sales
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 mb-6">
                      {plan.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-2">
                          <Check
                            className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5"
                            aria-hidden="true"
                          />
                          <span className="text-sm text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {plan.priceMonthly > 0 ? (
                      <Link
                        to={UPGRADE_PATH}
                        onClick={() => {
                          trackEvent("upgrade_clicked", {
                            plan: plan.planKey,
                            source: "upgrade_modal",
                          });
                          onClose();
                        }}
                        className={`block w-full px-6 py-3 text-center text-sm font-semibold transition-colors ${
                          isFirst
                            ? "adp-btn-primary"
                            : "bg-surface-2 hover:bg-surface-2/80 border border-border text-foreground"
                        }`}
                      >
                        Upgrade to {planLabel}
                      </Link>
                    ) : (
                      <a
                        href={SALES_HREF}
                        onClick={() => trackEvent("contact_sales_clicked", { plan: plan.planKey, source: "upgrade_modal" })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-6 py-3 text-center text-sm font-semibold bg-surface-2 hover:bg-surface-2/80 border border-border text-foreground transition-colors"
                      >
                        Contact sales
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer reassurance */}
          {!isLoading && upgradePlans.length > 0 && (
            <div className="p-4 border border-border bg-background">
              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                  <span>Cancel anytime — pro-rated refund for unused period</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                  <span>Instant activation — no waiting period</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                  <span>EU-resident billing, GDPR-compliant data residency</span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-2 text-center">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
